import type { UnifiedProductSummary } from '@noths/polaris-client-unified-product-service';
import type { ProductImageSize } from '@noths/polaris-client-utils';
import { getProductImageURL } from '@noths/polaris-client-utils';
import type { Product } from '@noths/polaris-design-system-ooux-interfaces';

import { toMinorUnits } from 'src/utils/toMinorUnits';

/**
 * UPS quotes one price per product and takes no currency argument, so every card it feeds is
 * priced in GBP regardless of the currency the rest of the page is in. Remove this, and take the
 * currency from `priceInfo.currencyCode`, once UPS carries the other three.
 */
const UNIFIED_PRODUCT_CURRENCY = 'GBP';

/**
 * Every size the CDN serves, as a total record of `ProductImageSize` so that a preset added
 * upstream fails to compile here rather than silently leaving its images at whatever size UPS
 * happened to return.
 */
const PRESET_SIZES: Record<ProductImageSize, true> = {
  category_listing: true,
  medium: true,
  micro: true,
  mini: true,
  normal: true,
  original: true,
  preview: true,
  thumb: true,
};

const RESOLVED_PRESET_SIZE = new RegExp(`/(?:${Object.keys(PRESET_SIZES).join('|')})_`);

/**
 * UPS answers with an already resolved size, typically `original`, rather than the `{size}` token
 * its own fixtures carry and that `getProductImageURL` expects. Putting the token back first means
 * both forms end up at the size the card asked for, instead of a 1024px original on every card.
 */
const toCardImageURL = (uri: string) =>
  getProductImageURL(uri.replace(RESOLVED_PRESET_SIZE, '/{size}_'), 'normal');

/**
 * UPS stores product URIs against the production origin in every environment, QA included, and
 * that origin is the apex, which 301s to `www`. A site relative path both keeps the link inside
 * whichever environment served the page and skips the redirect. `RecommendedProducts` takes the
 * same line for the same reason.
 */
const toSitePath = (uri: string) => {
  try {
    const { pathname, search } = new URL(uri);

    return `${pathname}${search}`;
  } catch {
    return uri;
  }
};

/**
 * Adapts a UPS product summary into the OOUX `Product` the design system consumes.
 *
 * The interface is deliberately semantic, so several UPS fields have no counterpart and are not
 * mapped: `salePercentage` because `ProductCard` derives the discount by comparing `price` with
 * `originalPrice`, `onSale` because the discount is the original price being present, and `isNew`
 * because it is card presentation rather than a property of the product.
 */
export const transformUnifiedProductToProduct = ({
  attributes,
  code,
  images,
  priceInfo,
  title,
  uri,
}: UnifiedProductSummary): Product => {
  const [image] = images;

  return {
    // Non-purchasable products are filtered out before they reach here, so everything the card
    // renders is in stock.
    availability: 'in_stock',
    code: String(code),
    currencyCode: UNIFIED_PRODUCT_CURRENCY,
    /* Empty alt because the card wraps the image in a link named by the title, so describing it
       again would announce the product twice. UPS supplies `altText`, which is deliberately not
       used for that reason. */
    media: image ? [{ alt: '', type: 'image', url: toCardImageURL(image.uri) }] : [],
    /* Taken from `priceInfo` rather than `preSalePrices` so that both halves of the comparison the
       card makes come from one currency and one unit system. */
    ...(priceInfo.originalPrice !== null && {
      originalPrice: toMinorUnits(priceInfo.originalPrice),
    }),
    // The card shows the partner's name as plain text. `shortcode` and `url` are optional and
    // unrendered, so they are left out until something needs them.
    partner: { name: attributes.partnerName ?? '' },
    price: toMinorUnits(priceInfo.price),
    title,
    url: toSitePath(uri),
  };
};
